import { type WrappedInsightTotalEarningsLifetimeAttributes } from "@clipboard-health/contract-backend-main";
import { Image, Span, Text } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";

import coinIcon from "../../assets/coin-wrapped.png";

interface TotalEarningsLifetimeContentProps {
  attributes: WrappedInsightTotalEarningsLifetimeAttributes;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function TotalEarningsLifetimeContent(props: TotalEarningsLifetimeContentProps) {
  const { attributes } = props;

  return (
    <Stack flex={1} position="relative" overflow="hidden">
      {/* White streak effect - positioned to pass through coin center */}
      {/* Coin center is at approximately (62%, 50%) on this page */}
      <Box
        position="absolute"
        sx={{
          top: "-65%",
          left: "-50%",
          width: "200%",
          height: "200%",
          backgroundSize: "100% 100%",
          backgroundPosition: "35% 40%", // Adjusted to hit coin at 26.7% top, 62% center
          pointerEvents: "none",
        }}
      />

      {/* Text section - top left */}
      <Stack
        position="absolute"
        sx={{
          top: "12%",
          left: "7.7%",
        }}
        spacing={3}
      >
        <Text variant="body1" color="white">
          You earned
        </Text>
        <Text variant="h1" color="white" sx={{ fontSize: "4rem", lineHeight: 1 }}>
          {formatCurrency(attributes.earnings)}
        </Text>
        <Text variant="body1" color="white">
          in 2025
        </Text>
      </Stack>

      {/* Coin - positioned based on Figma: 357x357 at top 217px, left 55px, angle 2.9deg on 375x812 page */}
      <Box
        position="absolute"
        sx={{
          top: "26.7%", // 217 / 812
          left: "14.7%", // 55 / 375
        }}
      >
        <Image
          src={coinIcon}
          alt="Coin"
          width="95vw" // 357 / 375
          height="auto"
          sx={{ transform: "rotate(2.9deg)" }}
        />
      </Box>

      {/* Lifetime earnings text - bottom, staggered alignment */}
      <Stack
        position="absolute"
        sx={{
          bottom: "12%",
          left: "7.7%",
          right: "7.7%",
        }}
      >
        {/* First line - left aligned */}
        <Text variant="body1" color="white">
          In <Span sx={{ fontSize: "2rem" }}>{attributes.yearsWorkedClipboard} years</Span> on
          Clipboard,
        </Text>
        {/* Second line - right aligned */}
        <Text variant="body1" color="white" textAlign="right">
          you&apos;ve earned{" "}
          <Span sx={{ fontSize: "1.5rem" }}>{formatCurrency(attributes.lifetimeEarnings)}</Span>.
        </Text>
      </Stack>
    </Stack>
  );
}
